import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useLocalText } from '../common/useLocalText';
import { useLocalization } from '../../context/LocalizationContext';
import { Box, Grid, TextField, InputAdornment, Alert, Typography } from "@mui/material";
import { usePermissions } from '../../hooks/usePermissions';
import StatusTabs from '../approval/StatusTabs';
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PortalCard from './PortalCard';
import PortalSlotTile from './PortalSlotTile';
import SelectTypeModal from './SelectTypeModal';
import CreatePortalModal from './CreatePortalModal';
import EditPortalModal from './EditPortalModal';
import ConvertPortalToMediaModal from './ConvertPortalToMediaModal';
import ConvertToPortalModal from './ConvertToPortalModal';
import CreateMediaModal from '../media/CreateMediaModal';
import {
  createPortal,
  updatePortal,
  deletePortalFromBooth,
  convertPortalToMedia,
  convertPortalToCustom,
} from '../../api/portalApi';
import {
  createMediaForBooth,
  deleteBoothMedia,
  updateBoothMedia,
  convertMediaToPortal,
  convertMediaToCustom,
} from '../../api/boothApi';
import EditMediaModal from '../media/EditMediaModal';
import CreateCustomBoothItemModal from '../customItems/CreateCustomBoothItemModal';
import EditCustomBoothItemModal from '../customItems/EditCustomBoothItemModal';
import ConvertToCustomModal from '../customItems/ConvertToCustomModal';
import {
  createCustomBoothItem,
  updateCustomBoothItem,
  deleteCustomBoothItemFromBooth,
  convertCustomToPortal,
  convertCustomToMedia,
} from '../../api/customBoothItemApi';
import { v4 as uuidv4 } from "uuid";
import { useToast } from '../../context/ToastContext';
import { useApproval } from '../../context/ApprovalContext';
import { submitApproval } from '../../api/approvalApi';
import { CONTENT_TYPES } from '../../constants/contentTypes';

const PortalLinks = ({ id, booth, boothName, onBoothRefresh }) => {
  const { updateTranslation, t, locale } = useLocalization();
  const { canFeature } = usePermissions();
  const { addMySubmission } = useApproval();
  const canEdit = canFeature('/booth', 'booth-edit');
  const isBoothLocked = booth?.approvalStatus === 'pending' || booth?.approvalStatus === 'rejected';
  const [selectTypeOpen, setSelectTypeOpen] = useState(false);
  const [portalProposeEditMode, setPortalProposeEditMode] = useState(false);
  const [portalViewLiveMode, setPortalViewLiveMode] = useState(false);
  const [createPortalOpen, setCreatePortalOpen] = useState(false);
  const [editPortalOpen, setEditPortalOpen] = useState(false);
  const [selectedPortal, setSelectedPortal] = useState(null);
  // Generic convert flow — any itemType can be the source, any of the other two can be the
  // target (see docs\Booth_Item_Cross_Type_Conversion_Plan.md). convertSourceType is the
  // CONTENT_TYPES/'media' key of whatever card triggered it (portal/custom/media).
  const [convertPickerOpen, setConvertPickerOpen] = useState(false);
  const [convertSource, setConvertSource] = useState(null);
  const [convertSourceType, setConvertSourceType] = useState(null);
  const [convertToMediaOpen, setConvertToMediaOpen] = useState(false);
  const [convertToPortalOpen, setConvertToPortalOpen] = useState(false);
  const [convertToCustomOpen, setConvertToCustomOpen] = useState(false);
  const [createMediaOpen, setCreateMediaOpen] = useState(false);
  const [editMediaOpen, setEditMediaOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaProposeEditMode, setMediaProposeEditMode] = useState(false);
  const [mediaViewLiveMode, setMediaViewLiveMode] = useState(false);
  const [createCustomItemOpen, setCreateCustomItemOpen] = useState(false);
  const [editCustomItemOpen, setEditCustomItemOpen] = useState(false);
  const [editingCustomItem, setEditingCustomItem] = useState(null);
  const [customItemProposeEditMode, setCustomItemProposeEditMode] = useState(false);
  const [customItemViewLiveMode, setCustomItemViewLiveMode] = useState(false);
  const { toast } = useToast();

  const showSuccess = (msg) => toast.success(msg);
  const showError   = (msg) => toast.error(msg);

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [tabValue, setTabValue] = useState('live');
  const [highlight, setHighlight] = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const searchPlaceholder = useLocalText("Search portals & media...");

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(value), 300);
  };

  const boothItems = booth?.boothItems ?? [];
  const filledCount = boothItems.length;
  const tileCount = Math.max(filledCount, booth?.maxPortals ?? 4);

  // Portal/media items use contentStatus ('approved'|'draft'|null) for live/draft distinction.
  // approvalStatus ('pending'|'rejected') comes from the active CA record join in the SP.
  // Old items created before the status column was added have contentStatus=null — treat as live.
  const tabCounts = useMemo(() => ({
    live:     boothItems.filter((i) => !i.contentStatus || i.contentStatus === 'approved').length,
    pending:  boothItems.filter((i) => i.approvalStatus === 'pending').length,
    rejected: boothItems.filter((i) => i.approvalStatus === 'rejected').length,
    // Draft = truly unsubmitted content; excluded if it already has an active rejected proposal
    // (those belong in the Rejected tab where dismiss/resubmit actions are available).
    draft:    boothItems.filter((i) => i.contentStatus === 'draft' && i.approvalStatus !== 'rejected').length,
  }), [boothItems]);

  const filteredItems = useMemo(() => {
    let tabFiltered;
    switch (tabValue) {
      case 'live':
        tabFiltered = boothItems.filter((i) => !i.contentStatus || i.contentStatus === 'approved');
        break;
      case 'pending':
        tabFiltered = boothItems.filter((i) => i.approvalStatus === 'pending');
        break;
      case 'rejected':
        tabFiltered = boothItems.filter((i) => i.approvalStatus === 'rejected');
        break;
      default: // 'draft'
        // Exclude items with a rejected proposal — they live in the Rejected tab.
        tabFiltered = boothItems.filter((i) => i.contentStatus === 'draft' && i.approvalStatus !== 'rejected');
        break;
    }
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return tabFiltered;
    return tabFiltered.filter((item) => {
      if (item.type === CONTENT_TYPES.PORTAL) {
        return (
          item.localizedPairs?.values?.some((v) => v.value?.toLowerCase().includes(q)) ||
          item.portalLinks?.some((pl) => pl.externalLink?.toLowerCase().includes(q))
        );
      }
      if (item.type === CONTENT_TYPES.CUSTOM) {
        return (
          item.localizedPairs?.values?.some((v) => v.value?.toLowerCase().includes(q)) ||
          item.externalLink?.toLowerCase().includes(q)
        );
      }
      const title = (
        item.textLocalizations?.find((l) => l.localeId === locale)?.value ||
        item.textLocalizations?.[0]?.value ||
        t(item.textKey, "") ||
        ""
      ).toLowerCase();
      return (
        title.includes(q) ||
        item.linkLocalizations?.some((v) => v.mediaLink?.toLowerCase().includes(q))
      );
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boothItems, debouncedSearch, tabValue]);

  const openTypeSelector = () => {
    if (filledCount >= tileCount) return;
    setSelectTypeOpen(true);
  };

  const handleTypeSelect = (type) => {
    setSelectTypeOpen(false);
    if (type === "portal") setCreatePortalOpen(true);
    else if (type === "custom") setCreateCustomItemOpen(true);
    else setCreateMediaOpen(true);
  };

  // sharedTitle: [{ localeId, value }]
  // positions: [{ portalLink, thumbnailLocalizations }]
  // sharedMedia: { mediaTypeId, linkLocalizations }
  const handleCreatePortal = async (sharedTitle, positions, sharedMedia) => {
    try {
      const portalLinks = positions
        .map((position, i) => ({
          positionId: i,
          externalLink: position.portalLink || "",
          thumbnailMedia: {
            mediaTypeId: 1,
            linkLocalizations: (position.thumbnailLocalizations || []).filter((t) => t.mediaLink),
          },
        }))
        .filter((pl) => pl.externalLink || pl.thumbnailMedia.linkLocalizations.length > 0);

      await createPortal({
        boothId: parseInt(id),
        portalTypeId: 1,
        eventId: null,
        localizedPairs: {
          key: uuidv4(),
          values: (sharedTitle || []).filter((p) => p.value),
        },
        correspondingMedia: {
          mediaTypeId: sharedMedia?.mediaTypeId ?? 1,
          linkLocalizations: (sharedMedia?.linkLocalizations || []).filter((m) => m.mediaLink),
        },
        portalLinks,
      });
      await onBoothRefresh();
      showSuccess("Portal created and moved to Draft.");
      flashTab('draft');
    } catch (err) {
      console.error("Failed to create portal:", err);
      showError("Failed to add portal. Please try again.");
    }
  };

  const handleEditPortal = async (sharedTitle, positionData, sharedMedia) => {
    if (!selectedPortal) return;
    try {
      const portalLinks = positionData
        .map((pos, i) => {
          const orig = selectedPortal.portalLinks?.find((pl) => pl.positionId === i);
          const thumbnailLocs = (pos.thumbnailLocalizations || []).filter((t) => t.mediaLink);
          const hasContent = !!pos.portalLink || thumbnailLocs.length > 0;
          if (!hasContent) return null;
          return {
            id: orig?.id ?? 0,
            positionId: i,
            externalLink: pos.portalLink || "",
            thumbnailMedia: thumbnailLocs.length > 0
              ? {
                  id: orig?.thumbnailMedia?.id ?? 0,
                  mediaTypeId: 1,
                  textKey: null,
                  descriptionKey: null,
                  linkLocalizations: thumbnailLocs,
                }
              : null,
          };
        })
        .filter(Boolean);

      const payload = {
        boothId: parseInt(id),
        portalTypeId: 1,
        eventId: null,
        localizedPairs: {
          key: selectedPortal.localizedPairs?.key || uuidv4(),
          values: (sharedTitle || []).filter((p) => p.value),
        },
        correspondingMedia: {
          id: selectedPortal.correspondingMedia?.id ?? 0,
          mediaTypeId: sharedMedia.mediaTypeId,
          textKey: null,
          descriptionKey: null,
          linkLocalizations: (sharedMedia.linkLocalizations || []).filter((m) => m.mediaLink),
        },
        portalLinks: portalLinks.length > 0 ? portalLinks : null,
      };

      if (portalProposeEditMode) {
        const contentName = selectedPortal.localizedPairs?.values?.[0]?.value || `Portal ${selectedPortal.id}`;
        await submitApproval({
          contentType: CONTENT_TYPES.PORTAL,
          contentId: selectedPortal.id,
          contentName,
          actionType: 'edit',
          proposedData: JSON.stringify(payload),
          proposedSnapshot: JSON.stringify(payload),
        });
        showSuccess("Edit proposal submitted for approval");
        await onBoothRefresh();
        flashTab('pending');
      } else {
        await updatePortal(id, selectedPortal.id, payload);
        showSuccess("Portal updated successfully");
        await onBoothRefresh();
        flashTab('draft');
      }
    } catch (err) {
      console.error("Failed to update portal:", err);
      showError("Failed to update portal. Please try again.");
    }
  };


  const handleConvertClick = (item, itemType) => {
    setConvertSource(item);
    setConvertSourceType(itemType);
    setConvertPickerOpen(true);
  };

  const handleConvertTypeSelect = (targetType) => {
    setConvertPickerOpen(false);
    if (targetType === 'media') setConvertToMediaOpen(true);
    else if (targetType === CONTENT_TYPES.PORTAL) setConvertToPortalOpen(true);
    else setConvertToCustomOpen(true);
  };

  const closeConvertModals = () => {
    setConvertToMediaOpen(false);
    setConvertToPortalOpen(false);
    setConvertToCustomOpen(false);
    setConvertSource(null);
    setConvertSourceType(null);
  };

  const handleConvertToMedia = async (payload) => {
    if (!convertSource) return;
    try {
      const result = convertSourceType === CONTENT_TYPES.CUSTOM
        ? await convertCustomToMedia(id, convertSource.id, payload)
        : await convertPortalToMedia(id, convertSource.id, payload);
      await onBoothRefresh();
      if (result?.message) {
        addMySubmission(result.approvalId);
        showSuccess('Convert-to-Media request submitted for approval.');
        flashTab('pending');
      } else {
        showSuccess('Converted — new Media item created in Draft.');
        flashTab('draft');
      }
    } catch (err) {
      console.error('Failed to convert to media:', err);
      showError('Failed to convert to media. Please try again.');
    } finally {
      closeConvertModals();
    }
  };

  const handleConvertToPortal = async (sharedTitle, positionData, sharedMedia) => {
    if (!convertSource) return;
    try {
      const portalLinks = positionData
        .map((position, i) => ({
          positionId: i,
          externalLink: position.portalLink || "",
          thumbnailMedia: {
            mediaTypeId: 1,
            linkLocalizations: (position.thumbnailLocalizations || []).filter((t) => t.mediaLink),
          },
        }))
        .filter((pl) => pl.externalLink || pl.thumbnailMedia.linkLocalizations.length > 0);

      const payload = {
        portalTypeId: 1,
        eventId: null,
        localizedPairs: {
          key: uuidv4(),
          values: (sharedTitle || []).filter((p) => p.value),
        },
        correspondingMedia: {
          mediaTypeId: sharedMedia?.mediaTypeId ?? 1,
          linkLocalizations: (sharedMedia?.linkLocalizations || []).filter((m) => m.mediaLink),
        },
        // For a Custom source, the backend derives PortalLinks itself from the item's own data
        // and ignores this field (decision #3 — carried over automatically, not admin-editable
        // here); this is only actually used for a Media source, which has no links to preserve.
        portalLinks,
      };

      const result = convertSourceType === 'media'
        ? await convertMediaToPortal(id, convertSource.id, payload)
        : await convertCustomToPortal(id, convertSource.id, payload);
      await onBoothRefresh();
      if (result?.message) {
        addMySubmission(result.approvalId);
        showSuccess('Convert-to-Portal request submitted for approval.');
        flashTab('pending');
      } else {
        showSuccess('Converted — new Portal created in Draft.');
        flashTab('draft');
      }
    } catch (err) {
      console.error('Failed to convert to portal:', err);
      showError('Failed to convert to portal. Please try again.');
    } finally {
      closeConvertModals();
    }
  };

  const handleConvertToCustom = async (modalOutput) => {
    if (!convertSource) return;
    try {
      const payload = {
        customBoothItemTypeId: modalOutput.customBoothItemTypeId,
        localizedPairs: { key: uuidv4(), values: modalOutput.localizedPairs.values },
        descriptionPairs: modalOutput.descriptionPairs ? { key: uuidv4(), values: modalOutput.descriptionPairs.values } : null,
        correspondingMedia: modalOutput.correspondingMedia,
        links: buildLinksPayload(modalOutput.positions, null),
        startDateTime: modalOutput.startDateTime,
        endDateTime: modalOutput.endDateTime,
        modifiedBy: 'system',
      };

      const result = convertSourceType === 'media'
        ? await convertMediaToCustom(id, convertSource.id, payload)
        : await convertPortalToCustom(id, convertSource.id, payload);
      await onBoothRefresh();
      if (result?.message) {
        addMySubmission(result.approvalId);
        showSuccess('Convert-to-Custom request submitted for approval.');
        flashTab('pending');
      } else {
        showSuccess('Converted — new Custom item created in Draft.');
        flashTab('draft');
      }
    } catch (err) {
      console.error('Failed to convert to custom item:', err);
      showError('Failed to convert to custom item. Please try again.');
    } finally {
      closeConvertModals();
    }
  };

  const handleCreateMedia = async (payload) => {
    try {
      await createMediaForBooth(id, payload);
      // Inject new translations so cards show title immediately without a page refresh
      (payload.textLocalizations || []).forEach(({ localeId, value }) => {
        if (payload.textKey && value) updateTranslation(payload.textKey, localeId, value);
      });
      (payload.descriptionLocalizations || []).forEach(({ localeId, value }) => {
        if (payload.descriptionKey && value) updateTranslation(payload.descriptionKey, localeId, value);
      });
      await onBoothRefresh();
      showSuccess("Media created and moved to Draft.");
      flashTab('draft');
    } catch (err) {
      console.error("Failed to create media:", err);
      showError("Failed to add media. Please try again.");
    }
  };

  const handleEditMedia = async (payload) => {
    try {
      if (mediaProposeEditMode) {
        const contentName =
          editingMedia.textLocalizations?.[0]?.value || `Media ${editingMedia.id}`;
        // Include existing DB keys so ContentEditApplier can update the correct i18n rows.
        // MediaModal generates new UUIDs for textKey/descriptionKey on every save; we must
        // override with the real keys from the current media item.
        const enrichedSnapshot = {
          ...payload,
          id: editingMedia.id,
          textKey: editingMedia.textKey,
          descriptionKey: editingMedia.descriptionKey,
        };
        await submitApproval({
          contentType: CONTENT_TYPES.BOOTH_MEDIA,
          contentId: editingMedia.id,
          contentName,
          actionType: 'edit',
          proposedSnapshot: JSON.stringify(enrichedSnapshot),
        });
        showSuccess("Edit proposal submitted for approval");
        await onBoothRefresh();
        flashTab('pending');
        return;
      } else {
        await updateBoothMedia(id, editingMedia.id, {
          id: editingMedia.id,
          mediaTypeId: payload.mediaTypeId,
          textKey: editingMedia.textKey,
          descriptionKey: editingMedia.descriptionKey,
          linkLocalizations: payload.linkLocalizations,
          textLocalizations: payload.textLocalizations,
          descriptionLocalizations: payload.descriptionLocalizations,
        });

        if (editingMedia.textKey && payload.textLocalizations?.length) {
          payload.textLocalizations.forEach(({ localeId, value }) => {
            if (value) updateTranslation(editingMedia.textKey, localeId, value);
          });
        }

        if (editingMedia.descriptionKey && payload.descriptionLocalizations?.length) {
          payload.descriptionLocalizations.forEach(({ localeId, value }) => {
            if (value) updateTranslation(editingMedia.descriptionKey, localeId, value);
          });
        }

        showSuccess("Media updated successfully");
      }

      await onBoothRefresh();
      flashTab('draft');
    } catch (err) {
      console.error("Failed to update media:", err);
      showError("Failed to update media. Please try again.");
    }
  };

  // modalOutput.positions: [{ link, thumbnailLocalizations }] — 3 slots (Left/Center/Right),
  // same shape handleCreatePortal/handleEditPortal already build from CreatePortalModal.
  const buildLinksPayload = (positions, existingLinks) =>
    positions
      .map((pos, i) => {
        const orig = existingLinks?.find((l) => l.positionId === i);
        const thumbLocs = (pos.thumbnailLocalizations || []).filter((t) => t.mediaLink);
        const hasContent = !!pos.link || thumbLocs.length > 0;
        if (!hasContent) return null;
        return {
          id: orig?.id ?? 0,
          positionId: i,
          externalLink: pos.link || "",
          thumbnailMedia: thumbLocs.length > 0
            ? { id: orig?.thumbnailMedia?.id ?? 0, mediaTypeId: 1, textKey: null, descriptionKey: null, linkLocalizations: thumbLocs }
            : null,
        };
      })
      .filter(Boolean);

  const handleCreateCustomItem = async (modalOutput) => {
    try {
      const payload = {
        customBoothItemTypeId: modalOutput.customBoothItemTypeId,
        localizedPairs: { key: uuidv4(), values: modalOutput.localizedPairs.values },
        descriptionPairs: modalOutput.descriptionPairs ? { key: uuidv4(), values: modalOutput.descriptionPairs.values } : null,
        correspondingMedia: modalOutput.correspondingMedia,
        links: buildLinksPayload(modalOutput.positions, null),
        startDateTime: modalOutput.startDateTime,
        endDateTime: modalOutput.endDateTime,
        modifiedBy: 'system',
      };

      await createCustomBoothItem(id, payload);
      await onBoothRefresh();
      showSuccess("Custom item created and moved to Draft.");
      flashTab('draft');
    } catch (err) {
      console.error("Failed to create custom item:", err);
      showError("Failed to add custom item. Please try again.");
    }
  };

  const handleEditCustomItem = async (modalOutput) => {
    try {
      const payload = {
        customBoothItemTypeId: modalOutput.customBoothItemTypeId,
        // Included even though the update DTO has no such field (harmless extra JSON
        // property) — the approval comparison reads optionType directly to avoid an
        // id+lookup round trip; see CustomItemModal's own comment for why.
        optionType: modalOutput.optionType,
        localizedPairs: { key: editingCustomItem.localizedPairs?.key || uuidv4(), values: modalOutput.localizedPairs.values },
        descriptionPairs: modalOutput.descriptionPairs
          ? { key: editingCustomItem.descriptionPairs?.key || uuidv4(), values: modalOutput.descriptionPairs.values }
          : null,
        correspondingMedia: {
          id: editingCustomItem.correspondingMedia?.id ?? 0,
          mediaTypeId: modalOutput.correspondingMedia.mediaTypeId,
          textKey: null,
          descriptionKey: null,
          linkLocalizations: modalOutput.correspondingMedia.linkLocalizations,
        },
        links: buildLinksPayload(modalOutput.positions, editingCustomItem.links),
        startDateTime: modalOutput.startDateTime,
        endDateTime: modalOutput.endDateTime,
        modifiedBy: 'system',
      };

      if (customItemProposeEditMode) {
        const contentName = editingCustomItem.localizedPairs?.values?.[0]?.value || `Custom Item ${editingCustomItem.id}`;
        await submitApproval({
          contentType: CONTENT_TYPES.CUSTOM_BOOTH_ITEM,
          contentId: editingCustomItem.id,
          contentName,
          actionType: 'edit',
          proposedSnapshot: JSON.stringify(payload),
        });
        showSuccess("Edit proposal submitted for approval");
        await onBoothRefresh();
        flashTab('pending');
        return;
      }

      await updateCustomBoothItem(id, editingCustomItem.id, payload);
      showSuccess("Custom item updated successfully");
      await onBoothRefresh();
      flashTab('draft');
    } catch (err) {
      console.error("Failed to update custom item:", err);
      showError("Failed to update custom item. Please try again.");
    }
  };

  const handleDelete = async (boothId, itemId, itemType) => {
    try {
      if (itemType === "media") {
        await deleteBoothMedia(boothId, itemId);
      } else if (itemType === CONTENT_TYPES.CUSTOM) {
        await deleteCustomBoothItemFromBooth(boothId, itemId);
      } else {
        await deletePortalFromBooth(boothId, itemId);
      }
      await onBoothRefresh();
      showSuccess(
        itemType === "media" ? "Media deleted successfully"
          : itemType === CONTENT_TYPES.CUSTOM ? "Custom item deleted successfully"
          : "Portal deleted successfully"
      );
    } catch (err) {
      console.error("Failed to delete:", err);
      showError("Failed to delete. Please try again.");
    }
  };

  return (
    <Box sx={{ pt: 1 }}>
      {isBoothLocked && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
          This booth has a {booth.approvalStatus} approval. Portals, media, and custom items cannot be added or modified until the approval is resolved.
        </Alert>
      )}
      <StatusTabs items={boothItems} counts={tabCounts} value={tabValue} onChange={setTabValue} highlightTab={highlight.tab} highlightToken={highlight.token} />
      <Box sx={{ maxWidth: 320, mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder={searchPlaceholder}
          inputRef={inputRef}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "#85a5c6" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              bgcolor: "white",
              "& fieldset": { borderColor: "#dde6f0" },
              "&:hover fieldset": { borderColor: "#5383b3" },
              "&.Mui-focused fieldset": { borderColor: "#0a326d" },
            },
          }}
        />
      </Box>
      {canEdit && !isBoothLocked && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 16, color: '#64748b', flexShrink: 0, mt: '2px' }} />
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            This booth supports {tileCount} Wall Items. Once full, delete one to add a new one, or use Convert to change an existing item's type instead.
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {filteredItems.map((item) => {
          const isProposalTab = tabValue === 'pending' || tabValue === 'rejected';

          const portalEditRejected = (snap, itemId) => {
            const p = boothItems.find((bi) => bi.id === itemId) || item;
            setSelectedPortal(p);
            setPortalProposeEditMode(true);
            setEditPortalOpen(true);
          };
          const mediaEditRejected = (snap, itemId) => {
            const m = boothItems.find((bi) => bi.id === itemId) || item;
            setEditingMedia(m);
            setMediaProposeEditMode(true);
            setEditMediaOpen(true);
          };
          const customItemEditRejected = (snap, itemId) => {
            const c = boothItems.find((bi) => bi.id === itemId) || item;
            setEditingCustomItem(c);
            setCustomItemProposeEditMode(true);
            setEditCustomItemOpen(true);
          };

          let commonProps;
          if (item.type === CONTENT_TYPES.PORTAL) {
            commonProps = {
              itemType: CONTENT_TYPES.PORTAL,
              deletePortal: handleDelete,
              onEdit: (proposeMode) => { setSelectedPortal(item); setPortalProposeEditMode(!!proposeMode); setPortalViewLiveMode(false); setEditPortalOpen(true); },
              onViewLive: () => { setSelectedPortal(item); setPortalProposeEditMode(false); setPortalViewLiveMode(true); setEditPortalOpen(true); },
              onEditRejected: portalEditRejected,
              onConvert: handleConvertClick,
              onSuccess: (targetTab) => { onBoothRefresh(); if (targetTab) flashTab(targetTab); },
            };
          } else if (item.type === CONTENT_TYPES.CUSTOM) {
            commonProps = {
              itemType: CONTENT_TYPES.CUSTOM,
              deletePortal: handleDelete,
              onEdit: (proposeMode) => { setEditingCustomItem(item); setCustomItemProposeEditMode(!!proposeMode); setCustomItemViewLiveMode(false); setEditCustomItemOpen(true); },
              onViewLive: () => { setEditingCustomItem(item); setCustomItemProposeEditMode(false); setCustomItemViewLiveMode(true); setEditCustomItemOpen(true); },
              onEditRejected: customItemEditRejected,
              onConvert: handleConvertClick,
              onSuccess: (targetTab) => { onBoothRefresh(); if (targetTab) flashTab(targetTab); },
            };
          } else {
            commonProps = {
              itemType: 'media',
              deletePortal: handleDelete,
              onEdit: (proposeMode) => { setEditingMedia(item); setMediaProposeEditMode(!!proposeMode); setMediaViewLiveMode(false); setEditMediaOpen(true); },
              onViewLive: () => { setEditingMedia(item); setMediaProposeEditMode(false); setMediaViewLiveMode(true); setEditMediaOpen(true); },
              onEditRejected: mediaEditRejected,
              onConvert: handleConvertClick,
              onSuccess: (targetTab) => { onBoothRefresh(); if (targetTab) flashTab(targetTab); },
            };
          }

          // Pending/Rejected tabs: show only the proposal (snapshot) card.
          // Live/Draft tabs: show only the live card. Each tab shows its own status only.
          const cardItem = isProposalTab
            ? { ...item, boothId: id, _viewType: 'snapshot' }
            : { ...item, boothId: id };

          return (
            <Grid key={`${item.type}-${item.id}`} size={{ xs: 12, sm: 6, md: 4 }}>
              <PortalCard item={cardItem} {...commonProps} parentLocked={isBoothLocked} showActions={canEdit} />
            </Grid>
          );
        })}

        {/* Empty "+" tiles — hidden during search or when locked/no-edit-permission */}
        {!debouncedSearch.trim() && canEdit && !isBoothLocked && Array.from({ length: tileCount - filledCount }, (_, i) => (
          <Grid key={`empty-${i}`} size={{ xs: 12, sm: 6, md: 4 }}>
            <PortalSlotTile onClick={openTypeSelector} />
          </Grid>
        ))}
      </Grid>

      {debouncedSearch.trim() && filteredItems.length === 0 && (
        <Box sx={{ textAlign: "center", py: 6, color: "#85a5c6", fontWeight: 500 }}>
          No results match "{inputRef.current?.value ?? debouncedSearch}"
        </Box>
      )}

      <SelectTypeModal
        open={selectTypeOpen}
        onClose={() => setSelectTypeOpen(false)}
        onSelect={handleTypeSelect}
      />

      <CreatePortalModal
        open={createPortalOpen}
        onClose={() => setCreatePortalOpen(false)}
        onSave={handleCreatePortal}
        positionCount={3}
        boothName={boothName}
      />

      <EditPortalModal
        open={editPortalOpen}
        onClose={() => { setEditPortalOpen(false); setSelectedPortal(null); setPortalProposeEditMode(false); setPortalViewLiveMode(false); }}
        portals={selectedPortal ? [selectedPortal] : []}
        onSave={handleEditPortal}
        positionCount={3}
        boothName={boothName}
        readOnly={portalViewLiveMode}
      />

      {/* Generic Convert flow — picker shows the OTHER two types, then routes to the matching
          prefilled modal. ConvertPortalToMediaModal is reused unchanged for a Custom source too —
          Custom's localizedPairs/correspondingMedia fields match Portal's field-for-field, so its
          `portal` prop name is a slight misnomer but the extraction logic works identically. */}
      <SelectTypeModal
        open={convertPickerOpen}
        onClose={() => { setConvertPickerOpen(false); setConvertSource(null); setConvertSourceType(null); }}
        onSelect={handleConvertTypeSelect}
        excludeKey={convertSourceType}
        title="Convert To"
      />

      <ConvertPortalToMediaModal
        open={convertToMediaOpen}
        onClose={closeConvertModals}
        onSave={handleConvertToMedia}
        portal={convertSource}
      />

      <ConvertToPortalModal
        open={convertToPortalOpen}
        onClose={closeConvertModals}
        onSave={handleConvertToPortal}
        source={convertSource}
        sourceType={convertSourceType}
        boothName={boothName}
      />

      <ConvertToCustomModal
        open={convertToCustomOpen}
        onClose={closeConvertModals}
        onSave={handleConvertToCustom}
        source={convertSource}
        sourceType={convertSourceType}
      />

      <CreateMediaModal
        open={createMediaOpen}
        onClose={() => setCreateMediaOpen(false)}
        onSave={handleCreateMedia}
      />

      <EditMediaModal
        open={editMediaOpen}
        onClose={() => { setEditMediaOpen(false); setMediaProposeEditMode(false); setMediaViewLiveMode(false); }}
        data={editingMedia}
        onSave={handleEditMedia}
        readOnly={mediaViewLiveMode}
      />

      <CreateCustomBoothItemModal
        open={createCustomItemOpen}
        onClose={() => setCreateCustomItemOpen(false)}
        onSave={handleCreateCustomItem}
      />

      <EditCustomBoothItemModal
        open={editCustomItemOpen}
        onClose={() => { setEditCustomItemOpen(false); setCustomItemProposeEditMode(false); setCustomItemViewLiveMode(false); }}
        data={editingCustomItem}
        onSave={handleEditCustomItem}
        readOnly={customItemViewLiveMode}
      />

    </Box>
  );
};

export default PortalLinks;
